import api from "@/lib/api";
import { speak as browserSpeak, stopSpeak as browserStop, pickVoice, voicesReady } from "@/lib/speech";

let currentAudio = null;
let progressTimer = null;
let runId = 0;

export const VOICE_SPEEDS = { fast: 1.25, normal: 1, slow: 0.85, very_slow: 0.7 };
export const getVoiceSpeed = () => {
  const v = localStorage.getItem("aa_voice_speed");
  return VOICE_SPEEDS[v] ? v : "slow";
};
export const setVoiceSpeed = (v) => localStorage.setItem("aa_voice_speed", v);
const rate = () => VOICE_SPEEDS[getVoiceSpeed()];
const browserRate = () => 0.92 * rate();

// Read maths symbols as the words a child hears, so 9-11 year olds can follow
// the question even if they cannot read the symbol. This only reads what is on
// screen faithfully; it never tells the child which operation to choose.
function normalizeForSpeech(text, lang = "en") {
  if (!text) return text;
  const w = lang === "fr"
    ? { plus: " plus ", minus: " moins ", times: " fois ", div: " divisé par ", eq: " égale ", gt: " est plus grand que ", lt: " est plus petit que ", over: " sur ", sq: " carré" }
    : { plus: " plus ", minus: " minus ", times: " times ", div: " divided by ", eq: " equals ", gt: " is greater than ", lt: " is less than ", over: " over ", sq: " squared" };
  let t = text;
  t = t.replace(/(\d)\s*\/\s*(\d)/g, `$1${w.over}$2`); // fractions: 3/4 -> three over four
  t = t.replace(/²/g, w.sq);
  t = t.replace(/[×✕✖]/g, w.times);
  t = t.replace(/(\d)\s*[xX]\s*(?=\d)/g, `$1${w.times}`); // 12 x 3 x 4
  const SUP = { "³": "3", "⁴": "4", "⁵": "5", "⁶": "6", "⁷": "7", "⁸": "8", "⁹": "9" };
  t = t.replace(/[³⁴⁵⁶⁷⁸⁹]/g, (c) => (lang === "fr" ? ` puissance ${SUP[c]}` : ` to the power of ${SUP[c]}`));
  t = t.replace(/[÷]/g, w.div);
  t = t.replace(/[+]/g, w.plus);
  t = t.replace(/[−–—]/g, w.minus); // unicode minus / en / em dash
  t = t.replace(/(\d)\s*-\s*(\d)/g, `$1${w.minus}$2`); // 7 - 3 (hyphen between numbers only)
  t = t.replace(/\s*=\s*/g, w.eq);
  t = t.replace(/\s*>\s*/g, w.gt);
  t = t.replace(/\s*<\s*/g, w.lt);
  return t.replace(/\s+/g, " ").trim();
}

// Same key as the backend cache (tts_service._key): repeat audio is a plain cacheable GET, no server work.
async function cacheId(text, lang) {
  const raw = `${text.trim().slice(0, 1000)}|nova|0.95|tts-1|mp3|${lang === "fr" ? "fr" : "en"}`;
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(raw));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

const optionLine = (word, i, o) => { const t = String(o).trim(); return `${word} ${String.fromCharCode(65 + i)} : ${t}${/[.!?]$/.test(t) ? "" : "."}`; };

// Online voice only: download the answer choices while the question is read, so they follow without a gap.
const prefetched = new Map();
function prefetch(text, lang) {
  if (lang === "fr" && hasDeviceVoice("fr")) return;
  const spoken = normalizeForSpeech(text, lang);
  const key = `${lang}|${spoken}`;
  if (prefetched.has(key)) return;
  if (prefetched.size > 40) prefetched.delete(prefetched.keys().next().value);
  prefetched.set(key, fetchOnline(spoken, lang).catch(() => { prefetched.delete(key); throw new Error("prefetch"); }));
}

function fetchAudio(spoken, lang) {
  const key = `${lang}|${spoken}`;
  if (prefetched.has(key)) return prefetched.get(key);
  return fetchOnline(spoken, lang);
}

async function fetchOnline(spoken, lang) {
  try {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tts/audio/${await cacheId(spoken, lang)}.mp3`);
    if (r.ok) return { data: await r.blob() };
  } catch (e) { /* not cached yet: generate below */ }
  return api.post("/tts", { text: spoken, lang }, { responseType: "blob" });
}

export async function playVoice(text, lang = "en") {
  stopVoice();
  await playClip(text, lang, () => {}, runId);
}

// Device voice with word-by-word progress (used when a recorded line is missing).
export async function playVoiceWords(text, lang, onWord) {
  stopVoice();
  await playClip(text, lang, onWord, runId);
}

const FR_UNITS = {
  mm: "millimètres", cm: "centimètres", m: "mètres", km: "kilomètres", g: "grammes", kg: "kilogrammes",
  l: "litres", L: "litres", ml: "millilitres", mL: "millilitres", cl: "centilitres", cL: "centilitres",
  "cm²": "centimètres carrés", "m²": "mètres carrés", "km²": "kilomètres carrés", "km/h": "kilomètres à l'heure", h: "heures",
};

// French voice: "Rs 1 250" -> "1250 roupies", "5 cm" -> "5 centimètres" (one spoken part per written word).
function frParts(toks) {
  const out = toks.map((t) => t);
  for (let i = 0; i < toks.length; i += 1) {
    const prevNum = /^\d+([.,]\d+)?$/.test(toks[i - 1] || "");
    const m = toks[i].match(/^(.+?)([.,;:!?)]*)$/);
    if (prevNum && m && FR_UNITS[m[1]]) out[i] = FR_UNITS[m[1]] + m[2];
  }
  for (let i = 0; i < toks.length; i += 1) {
    if (!/^\d{1,3}$/.test(toks[i])) continue;
    let j = i;
    while (/^\d{3}([.,;:!?)]|$)/.test(toks[j + 1] || "") && /^\d+$/.test(toks[j])) j += 1;
    if (j === i) continue;
    const num = toks.slice(i, j + 1).join("");
    for (let k = i; k < j; k += 1) out[k] = "";
    out[j] = num;
    i = j;
  }
  for (let i = 0; i < toks.length; i += 1) {
    if (toks[i] !== "Rs") continue;
    let j = i + 1;
    while (j < toks.length && out[j] === "") j += 1;
    if (j < toks.length && /^\d/.test(out[j])) {
      const m = out[j].match(/^(.+?)([.;:!?)]*)$/);
      out[i] = "";
      out[j] = `${m[1]} roupies${m[2]}`;
    } else out[i] = "roupies";
  }
  return out;
}

// Spoken form of each written word (maths symbols as words), so the voice position maps back to the word on screen.
function spokenParts(text, lang) {
  const toks = text.match(/\S+/g) || [];
  const base = lang === "fr" ? frParts(toks) : toks;
  return toks.map((t, i) => {
    if (/^[xX×]$/.test(t) && /\d$/.test(toks[i - 1] || "") && /^\d/.test(toks[i + 1] || "")) return lang === "fr" ? "fois" : "times";
    if (base[i] === "") return "";
    return normalizeForSpeech(base[i], lang) || base[i];
  });
}

// One audio element, unlocked by the child's first tap: phones (iPhone especially) block sound
// from audio elements created after an await. The first tap also unlocks the phone's own voice.
const SILENT = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
const player = typeof Audio !== "undefined" ? new Audio() : null;
let unlocked = false;
let settle = null;
export function unlockAudio() {
  if (!player || unlocked || currentAudio === player) return;
  player.src = SILENT;
  player.play().then(() => { unlocked = true; }).catch(() => {});
  const ss = typeof window !== "undefined" && window.speechSynthesis;
  if (ss && !ss.speaking) { const u = new SpeechSynthesisUtterance(" "); u.volume = 0; ss.speak(u); }
}
if (typeof document !== "undefined") {
  ["pointerdown", "touchend", "keydown"].forEach((ev) => document.addEventListener(ev, unlockAudio, { capture: true }));
}

function load(url, speed) {
  player.onplay = player.onended = player.onpause = player.onerror = player.onloadedmetadata = null;
  player.defaultPlaybackRate = speed;
  player.src = url;
  player.playbackRate = speed;
  currentAudio = player;
  return player;
}

// Phone/computer French voice (free, native). Word highlight follows the voice's own word events;
// if the device sends none, it steps through the words at the chosen speed.
function deviceSpeak(text, lang, onWord, myRun) {
  const ss = window.speechSynthesis;
  const parts = spokenParts(text, lang);
  const starts = [];
  let pos = 0;
  parts.forEach((p) => { starts.push(pos); pos += p.length + 1; });
  const total = (parts.length * 430) / rate();
  return new Promise((resolve) => {
    let done = false;
    let lastBoundary = 0;
    let timer = null;
    const finish = () => { if (done) return; done = true; clearInterval(timer); settle = null; resolve(); };
    settle = finish;
    const busy = ss.speaking || ss.pending;
    ss.cancel();
    const u = new SpeechSynthesisUtterance(parts.join(" "));
    const v = pickVoice(lang);
    u.lang = v?.lang?.replace("_", "-") || (lang === "fr" ? "fr-FR" : "en-GB");
    try { if (v) u.voice = v; } catch (e) { /* keep default voice for the language */ }
    deviceSpeak.current = u; // Chrome/Android drop utterances that are garbage-collected mid-sentence
    u.rate = Math.max(0.6, rate());
    u.pitch = 1.05;
    u.onboundary = (e) => {
      if (myRun !== runId || e.name === "sentence") return;
      lastBoundary = Date.now();
      let k = 0;
      while (k + 1 < starts.length && starts[k + 1] <= e.charIndex) k += 1;
      onWord(k);
    };
    u.onend = finish;
    u.onerror = finish;
    const t0 = Date.now();
    timer = setInterval(() => {
      if (myRun !== runId) { finish(); return; }
      if (!lastBoundary && Date.now() - t0 > 250) onWord(wordAtFraction(text, Math.min((Date.now() - t0) / total, 0.98)));
      if (Date.now() - t0 > total * 3 + 4000) { ss.cancel(); finish(); }
    }, 90);
    onWord(0);
    // Android cuts the next sentence if speak() follows cancel() immediately.
    if (busy) setTimeout(() => { if (!done) ss.speak(u); }, 120);
    else ss.speak(u);
  });
}

// Index of the written word reached after fraction f of the speaking time (voices without word events).
export function wordAtFraction(text, f) {
  const ws = (text || "").match(/\S+/g) || [];
  const total = ws.reduce((a, w) => a + wordWeight(w), 0) || 1;
  let acc = 0;
  let k = 0;
  ws.forEach((w, i) => { if (f >= acc / total) k = i; acc += wordWeight(w); });
  return k;
}

// One clip with word highlighting: French on the device's own French voice; otherwise the online voice.
async function playClip(text, lang, onWord, myRun) {
  if (lang === "fr") await voicesReady();
  if (myRun !== runId) return undefined;
  if (lang === "fr" && hasDeviceVoice("fr")) return deviceSpeak(text, lang, onWord, myRun);
  if (lang === "fr" && !sessionStorage.getItem("aa_fr_voice_tip")) {
    sessionStorage.setItem("aa_fr_voice_tip", "1");
    window.dispatchEvent(new Event("aa-no-fr-voice"));
  }
  return playChunk(text, lang, (f) => onWord(wordAtFraction(text, f)), myRun);
}

export function stopVoice() {
  runId += 1;
  if (progressTimer) { clearInterval(progressTimer); progressTimer = null; }
  if (currentAudio) {
    try { currentAudio.pause(); } catch (e) { /* noop */ }
    currentAudio = null;
  }
  if (settle) settle();
  browserStop();
}

// Does this phone/computer have its own voice for this language?
export function hasDeviceVoice(lang) {
  return !!pickVoice(lang);
}

// Online voice: plays one chunk, reporting progress 0..1; resolves when finished (or stopped).
function playChunk(text, lang, onProgress, myRun) {
  const spoken = normalizeForSpeech(text, lang);
  const words = (text.match(/\S+/g) || []).length || 1;
  return new Promise((resolve) => {
    const timed = () => {
      // browser voice: step through words at the chosen speed
      browserStop();
      let ended = false;
      browserSpeak(spoken, lang, browserRate(), () => { ended = true; });
      const total = (words * 420) / rate();
      const t0 = Date.now();
      progressTimer = setInterval(() => {
        if (myRun !== runId) { clearInterval(progressTimer); progressTimer = null; return resolve(); }
        const f = Math.min((Date.now() - t0) / total, ended ? 1 : 0.98);
        onProgress(f);
        if (f >= 1 || (ended && Date.now() - t0 > 300)) { clearInterval(progressTimer); progressTimer = null; onProgress(1); resolve(); }
      }, 80);
    };
    fetchAudio(spoken, lang).then(async (res) => {
      if (myRun !== runId) return resolve();
      const url = URL.createObjectURL(res.data);
      const audio = player ? load(url, rate()) : new Audio(url);
      let raf;
      const tick = () => {
        if (myRun !== runId) return;
        const d = audio.duration;
        if (d && isFinite(d)) onProgress(Math.min(audio.currentTime / d, 1));
        raf = requestAnimationFrame(tick);
      };
      audio.onplay = () => { raf = requestAnimationFrame(tick); };
      const finish = () => {
        cancelAnimationFrame(raf);
        settle = null;
        URL.revokeObjectURL(url);
        if (myRun === runId && currentAudio === audio) currentAudio = null;
        onProgress(1);
        resolve();
      };
      settle = finish;
      audio.onended = finish;
      audio.onerror = finish;
      try { await audio.play(); } catch (e) { settle = null; timed(); }
    }).catch(() => { if (myRun === runId) timed(); else resolve(); });
  });
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Approximate speaking time of one written word: numbers and maths symbols are read as longer words,
// punctuation adds a small pause.
export function wordWeight(w) {
  const letters = (w.match(/[\p{L}\d]/gu) || []).length;
  const digits = (w.match(/\d/g) || []).length;
  let x = letters + 1 + digits * 2;
  if (/[+×÷=<>−]/.test(w) && letters === 0) x += 5;
  if (/\d\/\d/.test(w)) x += 4;
  if (/[.!?:;,]["»”’']?$/.test(w)) x += 2;
  return x;
}

// Tap-to-hear: say only the tapped word.
export function speakWord(word, lang = "en") {
  const clean = word.replace(/^[^\p{L}\d]+|[^\p{L}\d%]+$/gu, "");
  if (clean) playVoice(clean, lang);
}

export function speakWordAt(text, k, lang = "en") {
  speakWord((text.match(/\S+/g) || [])[k] || "", lang);
}

// Splits a question into sentences (keeps decimals/thousands like 3.5 or 3,236 together).
export function splitSentences(text) {
  return (text || "").split(/(?<=[.!?:])\s+/).map((p) => p.trim()).filter(Boolean);
}

// Read-along: reads the question (onQuestionProgress(k) = index of the written word being spoken),
// then each answer choice as its own clip so no choice is skipped (onOption(i | null)).
export async function readAlongSequence(question, options, lang, { onQuestionProgress, onOption, onDone }) {
  stopVoice();
  const myRun = runId;
  const word = lang === "fr" ? "Réponse" : "Answer";
  if (lang === "fr") await voicesReady();
  if (myRun !== runId) return;
  options.forEach((o, i) => prefetch(optionLine(word, i, o), lang));
  onQuestionProgress(0);
  await playClip(question, lang, onQuestionProgress, myRun);
  if (myRun !== runId) return;
  onQuestionProgress(null);
  await wait(1000);
  for (let i = 0; i < options.length; i += 1) {
    if (myRun !== runId) return;
    onOption(i);
    await playClip(optionLine(word, i, options[i]), lang, () => {}, myRun);
    if (myRun !== runId) return;
    if (i + 1 < options.length) await wait(500);
  }
  if (myRun === runId) { onOption(null); onDone(); }
}

export const answerSpeech = (letterIndex, text, lang) => `${lang === "fr" ? "Réponse" : "Answer"} ${String.fromCharCode(65 + letterIndex)} : ${text}`;
